import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  MONEY_INVALID_ERROR,
  canonicalMoneyOrNull,
  moneyPreviewLabel,
  parseMoneyToCents,
} from "@/lib/contractMoney";

const CONTRACT_TYPES = [
  ["installation", "Instalación"],
  ["public_tender", "Licitación pública"],
  ["framework", "Marco"],
  ["maintenance", "Mantenimiento"],
  ["mixed", "Mixto"],
  ["internal", "Interno"],
  ["other", "Otro"],
] as const;

const BILLING_STRATEGIES = [
  ["milestones", "Por hitos"],
  ["periodic_certifications", "Por certificaciones periódicas"],
  ["mixed", "Mixta: hitos y certificaciones"],
  ["single_invoice", "Factura única"],
  ["on_demand", "Puntual / bajo demanda"],
  ["non_billable", "No facturable"],
] as const;

const BILLING_REQUIREMENT_MODES = [
  ["milestones_required", "Facturación requerida"],
  ["optional_billable", "Facturable opcional"],
  ["account_current_required", "Cuenta corriente requerida"],
  ["no_billing_required", "Sin facturación"],
] as const;

const BILLING_CHANNELS = [
  ["email", "Email"],
  ["face", "FACe"],
  ["portal", "Portal"],
  ["other", "Otro"],
] as const;

const CONTRACT_DOCUMENT_STATUSES = [
  ["contract_pending", "Contrato pendiente"],
  ["contract_signed", "Contrato firmado"],
  ["contract_not_required", "Contrato no requerido"],
  ["contract_unknown", "Desconocido"],
] as const;

const BILLING_CONFIG_STATUSES = [
  ["billing_not_configured", "Facturación sin configurar"],
  ["billing_provisional", "Facturación provisional"],
  ["billing_validated", "Facturación validada"],
] as const;

const MAINTENANCE_PAYMENT_STATUSES = [
  ["account_current", "Cuenta corriente"],
  ["payment_pending", "Pago pendiente"],
  ["payment_overdue", "Pago vencido"],
  ["unknown", "Desconocido"],
] as const;

const PAYMENT_METHODS = [
  ["transfer", "Transferencia"],
  ["confirming", "Confirming"],
  ["promissory_note", "Pagaré"],
  ["other", "Otro"],
  ["cash", "Efectivo"],
] as const;

const PAYMENT_TERMS = [
  ["invoice_date", "Fecha de factura"],
  ["end_of_month", "Fin de mes"],
  ["client_acceptance", "Aceptación del cliente"],
  ["other", "Otro"],
] as const;

// Server error codes mapped to user-facing messages for the draft edit path.
export const EDIT_CONTRACT_ERROR_MAP: Record<string, string> = {
  CONTRACT_STALE_UPDATE:
    "El contrato ha sido modificado por otro usuario. Recarga la página e inténtalo de nuevo.",
  CONTRACT_NOT_DRAFT:
    "Solo los contratos en borrador pueden editarse desde esta acción.",
  CONTRACT_ARCHIVED: "El contrato está archivado y no permite cambios.",
  CONTRACT_AMOUNT_INVALID: MONEY_INVALID_ERROR,
  CONTRACT_ECONOMIC_RULE_EDIT_BLOCKED_AFTER_EXECUTION:
    "Con ejecución económica, no se puede cambiar el importe base ni la moneda. El resto de la configuración se aplica a operaciones futuras y no modifica hitos, certificaciones ni importes históricos.",
  CONTRACT_BILLING_STRATEGY_MODE_CONFLICT:
    "La estrategia y la obligación de facturación no son compatibles. Ajusta ambas en la misma edición para configurar las operaciones futuras.",
  BILLING_CHANNEL_REQUIRED:
    "Selecciona un canal de facturación para este contrato facturable.",
  BILLING_CHANNEL_EMAIL_REQUIRED:
    "El bill_to no tiene email de facturación; completa ese dato antes de elegir Email.",
  BILLING_CHANNEL_EMAIL_INVALID: "El email de facturación no es válido.",
  BILLING_CHANNEL_EMAIL_OVERRIDE_REQUIRES_EMAIL_CHANNEL:
    "El email de facturación específico solo aplica al canal Email.",
  SALES_OWNER_NOT_ELIGIBLE: "El responsable comercial no es válido para este rol.",
  PM_NOT_ELIGIBLE: "El Project Manager no es válido para este rol.",
  BILLING_STRATEGY_MIXED_REQUIRES_VALID_PCT:
    "La estrategia mixta requiere un porcentaje de certificaciones entre 1 y 99.",
};

export function editContractErrorMessage(rawMessage: string): string {
  const code = (rawMessage ?? "").trim();
  if (EDIT_CONTRACT_ERROR_MAP[code]) return EDIT_CONTRACT_ERROR_MAP[code];
  for (const [codeKey, message] of Object.entries(EDIT_CONTRACT_ERROR_MAP)) {
    if (code.includes(codeKey)) return message;
  }
  return "No se pudo guardar el contrato. Revisa los campos e inténtalo de nuevo.";
}

export interface EditableContractData {
  id: number;
  status: string;
  title: string;
  contractType: string;
  externalContractNumber: string | null;
  tenderReference: string | null;
  awardDate: string | null;
  signedDate: string | null;
  contractualStartDate: string | null;
  contractualEndDate: string | null;
  salesOwnerUserId: number | null;
  projectManagerUserId: number | null;
  includesMaintenance: boolean;
  updatedAt: string;
  // Flat on the customers.contracts.get response (not nested under economics).
  billingChannel: string | null;
  billingEmailOverride: string | null;
  billingChannelNotes: string | null;
  economics?: {
    originalContractAmount: string | null;
    currency: string;
    billingRequirementMode: string | null;
    billingStrategy: string | null;
    certificationAllocationPercentage: number | null;
    paymentMethod: string | null;
    paymentTermBasis: string | null;
    paymentTermDays: number | null;
    invoiceNotes: string | null;
    contractDocumentStatus: string | null;
    billingConfigStatus: string | null;
    maintenancePaymentStatus: string | null;
  } | null;
}

interface Props {
  contract: EditableContractData;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  canWriteEconomic: boolean;
}

// Safe "Editar contrato" dialog (PR1). Only draft contracts reach this
// component (the parent gates the action); the server enforces draft-only
// again via customers.contracts.updateDraft. Identifiers and audit fields
// are never part of the payload — only whitelisted commercial/economic
// fields are sent, plus the optimistic expectedUpdatedAt preserved from the
// loaded contract.
export function EditContractDialog({
  contract,
  open,
  onOpenChange,
  canWriteEconomic,
}: Props) {
  const utils = trpc.useUtils();
  const [title, setTitle] = useState(contract.title);
  const [contractType, setContractType] = useState(contract.contractType);
  const [tenderReference, setTenderReference] = useState(contract.tenderReference ?? "");
  const [externalContractNumber, setExternalContractNumber] = useState(contract.externalContractNumber ?? "");
  const [salesOwnerUserId, setSalesOwnerUserId] = useState(
    contract.salesOwnerUserId != null ? String(contract.salesOwnerUserId) : "",
  );
  const [projectManagerUserId, setProjectManagerUserId] = useState(
    contract.projectManagerUserId != null ? String(contract.projectManagerUserId) : "",
  );
  const [awardDate, setAwardDate] = useState(contract.awardDate ?? "");
  const [signedDate, setSignedDate] = useState(contract.signedDate ?? "");
  const [contractualStartDate, setContractualStartDate] = useState(contract.contractualStartDate ?? "");
  const [contractualEndDate, setContractualEndDate] = useState(contract.contractualEndDate ?? "");
  const [includesMaintenance, setIncludesMaintenance] = useState(contract.includesMaintenance);

  const economics = contract.economics ?? null;
  const [amountInput, setAmountInput] = useState(economics?.originalContractAmount ?? "");
  const [currency, setCurrency] = useState(economics?.currency ?? "EUR");
  const [billingRequirementMode, setBillingRequirementMode] = useState(economics?.billingRequirementMode ?? "");
  const [billingStrategy, setBillingStrategy] = useState(economics?.billingStrategy ?? "");
  const [certificationAllocationPercentage, setCertificationAllocationPercentage] = useState(
    economics?.certificationAllocationPercentage != null
      ? String(economics.certificationAllocationPercentage)
      : "",
  );
  const [billingChannel, setBillingChannel] = useState(contract.billingChannel ?? "");
  const [billingEmailOverride, setBillingEmailOverride] = useState(contract.billingEmailOverride ?? "");
  const [billingChannelNotes, setBillingChannelNotes] = useState(contract.billingChannelNotes ?? "");
  const [paymentMethod, setPaymentMethod] = useState(economics?.paymentMethod ?? "");
  const [paymentTermBasis, setPaymentTermBasis] = useState(economics?.paymentTermBasis ?? "");
  const [paymentTermDays, setPaymentTermDays] = useState(
    economics?.paymentTermDays != null ? String(economics.paymentTermDays) : "",
  );
  const [invoiceNotes, setInvoiceNotes] = useState(economics?.invoiceNotes ?? "");
  const [contractDocumentStatus, setContractDocumentStatus] = useState(economics?.contractDocumentStatus ?? "");
  const [billingConfigStatus, setBillingConfigStatus] = useState(economics?.billingConfigStatus ?? "");
  const [maintenancePaymentStatus, setMaintenancePaymentStatus] = useState(economics?.maintenancePaymentStatus ?? "");
  const [error, setError] = useState<string | null>(null);

  const salesOwnersQuery = trpc.customers.listAssignableSalesOwners.useQuery(undefined, {
    enabled: open,
  });
  const salesOwners = (salesOwnersQuery.data ?? []) as Array<{ id: number; name: string; email: string }>;
  const pmQuery = trpc.customers.contracts.listAssignableProjectManagers.useQuery(undefined, {
    enabled: open,
  });
  const projectManagers = (pmQuery.data ?? []) as Array<{ id: number; name: string; email: string }>;

  useEffect(() => {
    if (open) {
      setTitle(contract.title);
      setContractType(contract.contractType);
      setTenderReference(contract.tenderReference ?? "");
      setExternalContractNumber(contract.externalContractNumber ?? "");
      setSalesOwnerUserId(contract.salesOwnerUserId != null ? String(contract.salesOwnerUserId) : "");
      setProjectManagerUserId(contract.projectManagerUserId != null ? String(contract.projectManagerUserId) : "");
      setAwardDate(contract.awardDate ?? "");
      setSignedDate(contract.signedDate ?? "");
      setContractualStartDate(contract.contractualStartDate ?? "");
      setContractualEndDate(contract.contractualEndDate ?? "");
      setIncludesMaintenance(contract.includesMaintenance);
      setAmountInput(economics?.originalContractAmount ?? "");
      setCurrency(economics?.currency ?? "EUR");
      setBillingRequirementMode(economics?.billingRequirementMode ?? "");
      setBillingStrategy(economics?.billingStrategy ?? "");
      setCertificationAllocationPercentage(
        economics?.certificationAllocationPercentage != null
          ? String(economics.certificationAllocationPercentage)
          : "",
      );
      setBillingChannel(contract.billingChannel ?? "");
      setBillingEmailOverride(contract.billingEmailOverride ?? "");
      setBillingChannelNotes(contract.billingChannelNotes ?? "");
      setPaymentMethod(economics?.paymentMethod ?? "");
      setPaymentTermBasis(economics?.paymentTermBasis ?? "");
      setPaymentTermDays(economics?.paymentTermDays != null ? String(economics.paymentTermDays) : "");
      setInvoiceNotes(economics?.invoiceNotes ?? "");
      setContractDocumentStatus(economics?.contractDocumentStatus ?? "");
      setBillingConfigStatus(economics?.billingConfigStatus ?? "");
      setMaintenancePaymentStatus(economics?.maintenancePaymentStatus ?? "");
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contract.updatedAt]);

  const amountCents = amountInput.trim() === "" ? null : parseMoneyToCents(amountInput);
  const amountInvalid = amountInput.trim() !== "" && amountCents === null;
  const amountPreviewCents = amountCents;

  const updateDraft = trpc.customers.contracts.updateDraft.useMutation();

  function handleCancel() {
    setError(null);
    onOpenChange(false);
  }

  async function handleSave() {
    if (title.trim().length === 0) {
      setError("El título del contrato es obligatorio.");
      return;
    }
    if (contractualEndDate && contractualStartDate && contractualEndDate < contractualStartDate) {
      setError("La fecha de fin no puede ser anterior a la de inicio.");
      return;
    }
    if (amountInvalid) {
      setError(MONEY_INVALID_ERROR);
      return;
    }

    // Whitelisted payload: commercial + economic fields only. The optimistic
    // expectedUpdatedAt from the loaded contract is preserved; no identifiers
    // or audit fields are ever sent.
    const payload: Record<string, unknown> = {
      contractId: contract.id,
      expectedUpdatedAt: contract.updatedAt,
      title: title.trim(),
      contractType,
      externalContractNumber: externalContractNumber.trim() || null,
      tenderReference: tenderReference.trim() || null,
      awardDate: awardDate || null,
      signedDate: signedDate || null,
      contractualStartDate: contractualStartDate || null,
      contractualEndDate: contractualEndDate || null,
      salesOwnerUserId: salesOwnerUserId && salesOwnerUserId !== "none" ? Number(salesOwnerUserId) : null,
      projectManagerUserId: projectManagerUserId && projectManagerUserId !== "none" ? Number(projectManagerUserId) : null,
      includesMaintenance,
    };
    if (canWriteEconomic) {
      payload.originalContractAmount = canonicalMoneyOrNull(amountInput);
      payload.currency = currency;
      payload.billingRequirementMode = (billingRequirementMode || null);
      payload.billingStrategy = (billingStrategy || null);
      if (billingStrategy === "mixed" && certificationAllocationPercentage !== "") {
        payload.certificationAllocationPercentage = Number(certificationAllocationPercentage);
      }
      payload.paymentMethod = (paymentMethod || null);
      payload.paymentTermBasis = (paymentTermBasis || null);
      payload.paymentTermDays = paymentTermDays !== "" ? Number(paymentTermDays) : null;
      payload.invoiceNotes = invoiceNotes.trim() || null;
      payload.contractDocumentStatus = (contractDocumentStatus || null);
      payload.billingConfigStatus = (billingConfigStatus || null);
      payload.maintenancePaymentStatus = (maintenancePaymentStatus || null);
      payload.billingChannel = (billingChannel || null);
      // An email override only makes sense for the "email" channel; carrying a
      // stale override across a channel switch would fail server validation
      // (BILLING_CHANNEL_EMAIL_OVERRIDE_REQUIRES_EMAIL_CHANNEL) instead of
      // silently persisting it.
      payload.billingEmailOverride = billingChannel === "email" ? (billingEmailOverride.trim() || null) : null;
      payload.billingChannelNotes = billingChannelNotes.trim() || null;
    }

    try {
      await updateDraft.mutateAsync(payload as Parameters<typeof updateDraft.mutateAsync>[0]);
      toast.success("Contrato actualizado.");
      // Wait for active contract-detail consumers (including the billing
      // channel editor) to receive the new updatedAt before closing. This is
      // still strict optimistic locking: a competing edit keeps its stale token
      // and must receive CONTRACT_STALE_UPDATE.
      await Promise.all([
        utils.customers.contracts.get.invalidate({ contractId: contract.id }),
        utils.customers.contracts.listInstallations.invalidate({ contractId: contract.id }),
        utils.installations.list.invalidate(),
        utils.installations.getById.invalidate(),
      ]);
      onOpenChange(false);
    } catch (err: any) {
      setError(editContractErrorMessage(err?.message ?? ""));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-xl overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar contrato</DialogTitle>
          <DialogDescription>
            Solo los contratos en borrador pueden editarse desde esta acción. Tras la primera ejecución, los cambios de configuración se aplican a operaciones futuras; los hitos, certificaciones e importes históricos no se recalculan. Si cambias estrategia u obligación de facturación, establece valores compatibles en la misma edición.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ec-title">Título *</Label>
            <Input
              id="ec-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={contractType} onValueChange={setContractType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTRACT_TYPES.map(([v, l]) => (
                    <SelectItem key={v} value={v}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ec-ext">Número contractual externo</Label>
              <Input
                id="ec-ext"
                value={externalContractNumber}
                onChange={(e) => setExternalContractNumber(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ec-tender">Referencia de licitación</Label>
            <Input
              id="ec-tender"
              value={tenderReference}
              onChange={(e) => setTenderReference(e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Responsable comercial</Label>
              <Select value={salesOwnerUserId} onValueChange={setSalesOwnerUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona comercial" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {salesOwners.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Project Manager</Label>
              <Select value={projectManagerUserId} onValueChange={setProjectManagerUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona PM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {projectManagers.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ec-award">Fecha de adjudicación</Label>
              <Input id="ec-award" type="date" value={awardDate} onChange={(e) => setAwardDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ec-signed">Fecha de firma</Label>
              <Input id="ec-signed" type="date" value={signedDate} onChange={(e) => setSignedDate(e.target.value)} />
            </div>
          </div>
          <section aria-labelledby="ec-contract-commitment" className="space-y-3 rounded-md border p-3">
            <div>
              <h3 id="ec-contract-commitment" className="text-sm font-semibold">Compromiso contractual (opcional)</h3>
              <p className="text-xs text-muted-foreground">Estas fechas representan el plazo contractual del contrato. La planificación de cada instalación/fase se gestiona por separado.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ec-start">Inicio contractual</Label>
              <Input
                id="ec-start"
                type="date"
                value={contractualStartDate}
                onChange={(e) => setContractualStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ec-end">Fin contractual</Label>
              <Input
                id="ec-end"
                type="date"
                value={contractualEndDate}
                onChange={(e) => setContractualEndDate(e.target.value)}
              />
            </div>
          </section>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includesMaintenance}
              onChange={(e) => setIncludesMaintenance(e.target.checked)}
            />
            Incluye mantenimiento
          </label>

          {canWriteEconomic && (
            <div className="space-y-4 rounded-md border p-3">
              <div className="space-y-1.5">
                <Label htmlFor="ec-amount">Importe contractual sin IVA</Label>
                <Input
                  id="ec-amount"
                  type="text"
                  inputMode="decimal"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                  placeholder="Ej. 28.228,25"
                />
                {amountInvalid ? (
                  <p className="text-xs text-destructive">{MONEY_INVALID_ERROR}</p>
                ) : amountPreviewCents != null ? (
                  <p className="text-xs text-muted-foreground">
                    {moneyPreviewLabel(amountPreviewCents, currency)}
                  </p>
                ) : null}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ec-currency">Moneda</Label>
                  <Input
                    id="ec-currency"
                    value={currency}
                    maxLength={3}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Modo de facturación</Label>
                  <Select value={billingRequirementMode} onValueChange={setBillingRequirementMode}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona modo" />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_REQUIREMENT_MODES.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Estrategia de facturación</Label>
                <Select value={billingStrategy} onValueChange={setBillingStrategy}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona estrategia" />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_STRATEGIES.map(([v, l]) => (
                      <SelectItem key={v} value={v}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {billingStrategy === "mixed" && (
                <div className="space-y-1.5">
                  <Label htmlFor="ec-cert-pct">% reservado para certificaciones (0–100)</Label>
                  <Input
                    id="ec-cert-pct"
                    type="number"
                    min={0}
                    max={100}
                    value={certificationAllocationPercentage}
                    onChange={(e) => setCertificationAllocationPercentage(e.target.value)}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Canal de facturación</Label>
                <Select value={billingChannel} onValueChange={setBillingChannel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona canal" />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_CHANNELS.map(([v, l]) => (
                      <SelectItem key={v} value={v}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {billingChannel === "email" && (
                <div className="space-y-1.5">
                  <Label htmlFor="ec-billing-email">Email de facturación específico</Label>
                  <Input
                    id="ec-billing-email"
                    type="email"
                    value={billingEmailOverride}
                    onChange={(e) => setBillingEmailOverride(e.target.value)}
                    placeholder="Déjalo vacío para usar el email del bill_to"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="ec-billing-notes">Notas del canal de facturación</Label>
                <Textarea
                  id="ec-billing-notes"
                  value={billingChannelNotes}
                  onChange={(e) => setBillingChannelNotes(e.target.value)}
                  maxLength={2000}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Estado del documento contractual</Label>
                  <Select value={contractDocumentStatus} onValueChange={setContractDocumentStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_DOCUMENT_STATUSES.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Estado de configuración de facturación</Label>
                  <Select value={billingConfigStatus} onValueChange={setBillingConfigStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_CONFIG_STATUSES.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {includesMaintenance && (
                <div className="space-y-1.5">
                  <Label>Estado de pago de mantenimiento</Label>
                  <Select value={maintenancePaymentStatus} onValueChange={setMaintenancePaymentStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {MAINTENANCE_PAYMENT_STATUSES.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Método de pago</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona método" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Base de plazo</Label>
                  <Select value={paymentTermBasis} onValueChange={setPaymentTermBasis}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona base" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ec-days">Días de plazo</Label>
                  <Input
                    id="ec-days"
                    type="number"
                    min="0"
                    max="365"
                    value={paymentTermDays}
                    onChange={(e) => setPaymentTermDays(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ec-invoice-notes">Notas de factura</Label>
                <Textarea
                  id="ec-invoice-notes"
                  value={invoiceNotes}
                  onChange={(e) => setInvoiceNotes(e.target.value)}
                />
              </div>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="outline" disabled={updateDraft.isPending} onClick={handleCancel}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={updateDraft.isPending || title.trim().length === 0 || amountInvalid}
            onClick={handleSave}
          >
            {updateDraft.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
